import {useAppContext} from 'contexts/ApplicationContext';
import {useJobContext} from 'contexts/JobContext';
import {useEffect, useMemo, useState, useCallback} from 'react';
import {object, string, number, mixed} from 'yup';
import {DispatchMethod} from 'hooks';
import {cloneDeep, flatten} from 'lodash';
import {MIMETypes} from 'shared/MIMETypes';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {shallowEqual} from 'react-redux';
import {getPhoneRegex} from 'shared/validation/phoneNumber';
import {
    DepotFreightOptions,
    depotListState,
    selectedDepot,
    selectedDepotState,
    setDepotList,
} from 'store/customer/depotSlice';
import {useGetDepotsQuery} from 'store/customer/depotApi';
import {DateTime} from '@cabinetsbycomputer/datetime';
import {
    getDispatchMethod,
    useDispatchMethods,
} from 'hooks/Job/useDispatchMethods';

const depotDispatcFreightOptions = {
    [DepotFreightOptions.PICKUP]: [DispatchMethod.PICKUP],
    [DepotFreightOptions.DELIVERY]: [
        DispatchMethod.FREIGHT_CUSTOM_ADDRESS,
        DispatchMethod.FREIGHT_DELIVERY_ADDRESS,
    ],
    [DepotFreightOptions.PICKUP_AND_DELIVERY]: [
        DispatchMethod.PICKUP,
        DispatchMethod.FREIGHT_CUSTOM_ADDRESS,
        DispatchMethod.FREIGHT_DELIVERY_ADDRESS,
    ],
};

const customValidator = (data, check, message) => {
    const messages = [];

    data.forEach((file) => {
        if (!check(file)) {
            messages.push(message(file));
        }
    });

    return messages;
};

const jobFormSchema = (
    userProfile,
    places,
    attachments,
    hideDeliveryPreferences
) => {
    let countryName = null;
    let states = [];

    if (userProfile.hasOwnProperty('countryName')) {
        countryName = userProfile.countryName;

        const allStates =
            places.hasOwnProperty('Countries') &&
            places.Countries.hasOwnProperty(userProfile.countryName)
                ? places.Countries[userProfile.countryName]
                : [];

        states = allStates.map((state) => state.id);
    }

    const phoneMatch = getPhoneRegex(countryName);

    const checkFreight = (dispatch) =>
        dispatch == DispatchMethod.FREIGHT_CUSTOM_ADDRESS ||
        dispatch == DispatchMethod.FREIGHT_DELIVERY_ADDRESS;

    const requestedDeliveryDateValidation = {
        requestedDeliveryDate: string().required(
            'Please select a requested delivery date.'
        ),
    };

    return object().shape({
        jobName: string().required('Please enter a Job name'),
        contactNumber: string().matches(
            phoneMatch,
            'Please enter a valid phone number'
        ),
        dispatch: number(),
        street: string().when('dispatch', {
            is: (dispatch) =>
                checkFreight(dispatch) && !hideDeliveryPreferences,
            then: (schema) =>
                schema.required('Please enter your street address'),
        }),
        suburb: string().when('dispatch', {
            is: (dispatch) =>
                checkFreight(dispatch) && !hideDeliveryPreferences,
            then: (schema) => schema.required('Please enter your suburb'),
        }),
        postcode: number().when('dispatch', {
            is: (dispatch) =>
                checkFreight(dispatch) && !hideDeliveryPreferences,
            then: (schema) =>
                schema
                    .positive('Please enter a valid Postcode')
                    .typeError('Please enter a valid Postcode')
                    .required('Please enter your Postcode'),
            // use min() max() to define range of postcodes depending upon the country.
        }),
        state: number().when('dispatch', {
            is: (dispatch) =>
                checkFreight(dispatch) && !hideDeliveryPreferences,
            then: (schema) =>
                schema
                    .required('Please enter your state')
                    .oneOf(states, 'Please enter a valid State'),
            // States stored in static json file in frontend as these are publicly available data and to reduce network overhead
        }),
        files: mixed()
            .nullable()
            .notRequired()
            .test(
                'FILE_NUMBER',
                'Too many files, only 10 files are allowed per job',
                (value) => value.length + attachments <= 10
            )
            .test(
                'FILE_SIZE',
                'The Maximum supported file size is 5 MB.',
                (value, {createError, path}) => {
                    const messages = customValidator(
                        value,
                        (file) => file.size / (1024 * 1024) < 5,
                        (file) =>
                            `"${file.name}" is unable to be uploaded as "${(
                                file.size /
                                (1024 * 1024)
                            ).toFixed(2)} MB" is too large.`
                    );

                    if (messages.length) {
                        messages.splice(
                            0,
                            0,
                            'The Maximum supported file size is 5 MB.'
                        );
                        return createError({
                            path,
                            message: messages.join('<br />'),
                        });
                    }

                    return true;
                }
            )
            .test(
                'FILE_FORMAT',
                'Invalid file format',
                (value, {createError, path}) => {
                    const supportedFormats = flatten(Object.values(MIMETypes));
                    const messages = customValidator(
                        value,
                        (file) => supportedFormats.includes(file.type),
                        (file) =>
                            `"${
                                file.name
                            }" is unable to be uploaded as ".${file.name
                                .split('.')
                                .pop()}" is an unsupported format.`
                    );

                    if (messages.length) {
                        return createError({
                            path,
                            message: messages.join('<br />'),
                        });
                    }

                    return true;
                }
            ),
        ...(userProfile?.allowDeliveryDateRequest &&
        userProfile?.requireDeliveryDateRequest &&
        !hideDeliveryPreferences
            ? requestedDeliveryDateValidation
            : {}),
    });
};

const initialValueMap = {
    jobName: 'name',
    jobRefCode: 'endCustomerName',
    contactNumber: 'endContactNumber',
    city: 'city',
    description: 'description',
    dispatch: 'dispatchMethod',
    address: 'address',
    suburb: 'suburb',
    postcode: 'postcode',
    state: 'addressState',
    depotId: 'depotId',
    street: 'address',
    requestedDeliveryDate: 'requestedDeliveryDate',
};

const defaultDeliveryOption = {
    [DepotFreightOptions.PICKUP]: DispatchMethod.PICKUP,
    [DepotFreightOptions.DELIVERY]: DispatchMethod.FREIGHT_DELIVERY_ADDRESS,
    [DepotFreightOptions.PICKUP_AND_DELIVERY]: DispatchMethod.PICKUP,
};

export const useJobForm = (jobId) => {
    const {userProfile, places} = useAppContext();
    const {job} = useJobContext();
    const dispatch = useAppDispatch();
    const [states, setStates] = useState([]);

    const dispatchMethod = getDispatchMethod(userProfile.defaultFreightOption);

    const [initialValues, setInitialValues] = useState({
        jobName: '',
        jobRefCode: '',
        contactNumber: userProfile.phNumber,
        description: '',
        requestedDeliveryDate: '',
        dispatch: dispatchMethod,
        depotId: userProfile.defaultDepotId,
        street: '',
        city: '',
        suburb: '',
        postcode: '',
        state: '',
        client: -1,
        files: [],
        attachments: [],
        packagingOptionIds: [],
        hasPackagingOptions: false,
        packagingOptionsLoaded: false,
        packagingOptionsValid: false,
    });
    const [attachmentsCount, setAttachmentsCount] = useState(0);
    const {data: depotList, isFetching, isLoading} = useGetDepotsQuery();
    const depots = useAppSelector(depotListState, shallowEqual);
    const depotSelected = useAppSelector(selectedDepotState, shallowEqual);
    const {dispatchMethods} = useDispatchMethods(depotSelected);

    const hideDeliveryPreferences = useMemo(() => {
        return (
            userProfile?.jobConfirmationEnabled &&
            !userProfile?.capturePickupFreightAtJobCreation
        );
    }, [userProfile]);

    useEffect(() => {
        if (depotList) {
            dispatch(setDepotList(depotList.depots));
            const depotId =
                (jobId ? job?.depotId : undefined) ??
                userProfile.defaultDepotId;
            dispatch(selectedDepot(depotId));
        }
    }, [depotList, job, jobId]);

    const JobSchema = useMemo(() => {
        const updatedInitialValues = cloneDeep(initialValues);

        const defaultDepot = depots?.find(
            (depot) => depot.id == userProfile.defaultDepotId
        );

        if (!jobId && defaultDepot) {
            const option =
                depotDispatcFreightOptions[defaultDepot.freight_pickup_option];

            if (!option.includes(updatedInitialValues.dispatch)) {
                const dispatchValue = option.length ? option[0] : 0;
                updatedInitialValues.dispatch = dispatchValue;
            }
        }

        setInitialValues(updatedInitialValues);

        return jobFormSchema(
            userProfile,
            places,
            attachmentsCount,
            hideDeliveryPreferences
        );
    }, [
        userProfile,
        places,
        attachmentsCount,
        depots,
        hideDeliveryPreferences,
    ]);

    const selectDepot = (setFieldValue, depotId) => {
        dispatch(selectedDepot(depotId));
        const selected = depots.find((depot) => depot.id === depotId);
        const dispatchValue =
            defaultDeliveryOption[selected.freight_pickup_option];

        setFieldValue('depotId', depotId);
        setFieldValue('dispatch', dispatchValue);
    };

    const updateAttachmentsCount = useCallback((count) => {
        setAttachmentsCount(count);
    }, []);

    useEffect(() => {
        if (jobId && job && Object.keys(job).length) {
            const updatedValues = cloneDeep(initialValues);

            Object.keys(initialValueMap).forEach((key) => {
                if (job.hasOwnProperty(initialValueMap[String(key)])) {
                    if (key === 'requestedDeliveryDate') {
                        const date = job[initialValueMap[String(key)]];

                        if (date != null) {
                            updatedValues[String(key)] = DateTime.parse(date)
                                .format()
                                .toString();
                        }
                    } else if (key == 'description') {
                        updatedValues.description = (
                            job.description ?? ''
                        ).replace(/<br\s*\/?>\r?\n?/gi, '\n');
                    } else {
                        updatedValues[String(key)] =
                            job[initialValueMap[String(key)]] === null
                                ? ''
                                : job[initialValueMap[String(key)]];
                    }
                }
            });

            if (
                job.hasOwnProperty('dispatchMethod') &&
                job.dispatchMethod === DispatchMethod.FREIGHT_TO_CLIENT_ADDRESS
            ) {
                // For existing jobs with freight to client's address - fallback to freight to address
                updatedValues.dispatch = DispatchMethod.FREIGHT_CUSTOM_ADDRESS;
            }

            let attachments = 0;

            if (job.attachments && Array.isArray(job.attachments)) {
                attachments = job.attachments.length;
            }

            setAttachmentsCount(attachments);
            setInitialValues(updatedValues);
        }
    }, [jobId, job]);

    useEffect(() => {
        if (userProfile) {
            const allStates =
                places.hasOwnProperty('Countries') &&
                places.Countries.hasOwnProperty(userProfile.countryName)
                    ? places.Countries[userProfile.countryName]
                    : [];

            setStates(allStates);
        }
    }, [userProfile, places]);

    return {
        states,
        dispatchMethods,
        userIsPremium:
            userProfile.hasOwnProperty('premium') && userProfile.premium == 1,
        allowFileUpload: userProfile?.allowFileUpload,
        allowDeliveryDateRequest: userProfile?.allowDeliveryDateRequest,
        minimumLeadTime: userProfile?.minimumLeadTime,
        initialValues,
        JobSchema,
        loading: isFetching || isLoading,
        updateAttachmentsCount,
        selectDepot,
        hideDeliveryPreferences,
    };
};
